import { AppNotification } from 'types/notification';
import getNotification from 'utils/getNotificaton';
import isCapturingDevNotifications from 'utils/isCapturingDevNotifications';
import useAuth from './useAuth';
import useSendEmailById, { FirebaseEmailTargetId } from './useSendEmailById';
import { useSelector } from 'store';
import { Event } from 'types/event';
import { CurrentEvent } from 'types/currentEvent';

export default function useCreateGroupNotification() {
  const { db, user } = useAuth();
  const sendEmailById = useSendEmailById();
  const currentEvent = useSelector((state) => state.currentEvent);

  /**
   * Sends a notification to ALL contacts subscribed to `notificationName`, plus any
   * contacts explicitly listed in `forcedRecipientIds`.
   *
   * --- FORCED RECIPIENTS ---
   * `forcedRecipientIds` is an escape hatch for key event roles (e.g. bandleader,
   * musical director) that must always receive certain notifications regardless of
   * whether they have opted in to the subscription feed.  These contacts receive
   * BOTH the in-app notification AND an email (if they have an email address on
   * file) unconditionally.
   *
   * Use this sparingly — prefer the normal subscription system wherever possible.
   * When adding a new role here, document it at the call site so it's easy to find.
   */
  const createGroupNotificaiton = async (
    notificationName: string,
    directData?: { mainData: Event },
    /** Contact IDs that bypass subscription checks and always receive all channels. */
    forcedRecipientIds: string[] = []
  ) => {
    const contactRef = db.collection('contacts');
    const message = getNotification(notificationName, directData as CurrentEvent || currentEvent);

    const users = await contactRef
      .where('notificationSubscriptions', 'array-contains', notificationName)
      // .where('email1', '==', 'todd@onthemove.com')
      .get();

    const usersEmail = await contactRef
      .where('notificationSubscriptionsEmail', 'array-contains', notificationName)
      // .where('email1', '==', 'todd@onthemove.com')
      .get();

    // --- DEV CAPTURE (see utils/isCapturingDevNotifications) ---
    // In dev with the config flag on, don't deliver anything to the real team —
    // just log a full summary of each lane and the content, then bail out.
    if (await isCapturingDevNotifications(db)) {
      const describe = (docs: typeof users.docs) =>
        docs.map((el) => {
          const c = el.data();
          return { id: el.id, name: c.name, email: c.email1 };
        });
      // eslint-disable-next-line no-console
      console.groupCollapsed(`[DEV] Notification captured — NOT sent to team: "${notificationName}"`);
      // eslint-disable-next-line no-console
      console.log('Subject:', message.subject);
      // eslint-disable-next-line no-console
      console.log('HTML:', message.html);
      // eslint-disable-next-line no-console
      console.log('In-app lane recipients:', describe(users.docs));
      // eslint-disable-next-line no-console
      console.log('Email lane recipients:', describe(usersEmail.docs));
      // eslint-disable-next-line no-console
      console.log('Forced recipients:', forcedRecipientIds.filter(Boolean));
      // eslint-disable-next-line no-console
      console.groupEnd();
      return { notificationResults: [], emailResults: [] };
    }

    const promises = users.docs.map((el) => {
      const toAdd: AppNotification = {
        ...message,
        createdBy: user?.name,
        status: 'unread',
        timestamp: new Date()
      };
      return contactRef.doc(el.id).collection('notifications').add(toAdd);
    });

    const promisesEmail = usersEmail.docs.map((el) => {
      const contact = el.data();
      const emailObject: FirebaseEmailTargetId = {
        targetId: contact.id,
        message
      };
      emailObject.message.html = emailObject.message.html.replace(/\n/g, '<br>');
      return contact.email1 ? sendEmailById(emailObject) : Promise.resolve();
    });

    // --- FORCED RECIPIENTS (bypass subscription check) ---
    // Fetch each forced contact and deliver both in-app + email unconditionally.
    const forcedPromises = forcedRecipientIds
      .filter(Boolean) // guard against empty/undefined IDs
      .map(async (contactId) => {
        const contactDoc = await contactRef.doc(contactId).get();
        if (!contactDoc.exists) return;

        const toAdd: AppNotification = {
          ...message,
          createdBy: user?.name,
          status: 'unread',
          timestamp: new Date()
        };

        const inAppPromise = contactRef.doc(contactId).collection('notifications').add(toAdd);

        const contact = contactDoc.data();
        const emailHtml = message.html.replace(/\n/g, '<br>');
        const emailPromise = contact?.email1
          ? sendEmailById({ targetId: contactId, message: { ...message, html: emailHtml } })
          : Promise.resolve();

        return Promise.all([inAppPromise, emailPromise]);
      });

    console.log('promises', promises);
    console.log('promisesEmail', promisesEmail);
    console.log('forcedRecipientIds', forcedRecipientIds);

    try {
      // Properly await all promises - both notification and email promises
      const [notificationResults, emailResults] = await Promise.all([
        Promise.all(promises),
        Promise.all(promisesEmail)
      ]);

      await Promise.all(forcedPromises);
      
      console.log('Notifications created:', notificationResults.length);
      console.log('Emails sent:', emailResults.length);
      
      return { notificationResults, emailResults };
    } catch (error) {
      console.error('Error sending notifications or emails:', error);
      throw error;
    }
  };

  return createGroupNotificaiton;
}
